import { useState, useCallback, useEffect, useMemo } from "react"
import { stableStringify } from "./objectsAreEqual"

export interface IItemSelectionState<T>
  extends Omit<ReturnType<typeof useSelectionState<T>>, "isSelected"> {
  isSelected?: boolean | undefined
}

export function useSelectionState<T>(
  items: T[],
  multiSelect: boolean = false,
  onSelectionChange?: (
    selection: ReturnType<typeof useSelectionState<T>>
  ) => void
) {
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(
    new Set()
  )

  const isSelected = useCallback(
    (item: T | string | number) => {
      const id = typeof item === "object" ? stableStringify(item) : String(item)
      return selectedIds.has(id)
    },
    [selectedIds]
  )

  const toggle = useCallback(
    (item: T) => {
      const id = stableStringify(item)
      setSelectedIds((prev) => {
        const newSet = new Set(prev)

        if (multiSelect) {
          if (newSet.has(id)) {
            newSet.delete(id)
          } else {
            newSet.add(id)
          }
        } else {
          newSet.clear()
          newSet.add(id)
        }
        return newSet
      })
    },
    [multiSelect]
  )

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (prev.size === items.length) {
        newSet.clear()
      } else {
        items.forEach((item) => {
          newSet.add(stableStringify(item))
        })
      }
      return newSet
    })
  }, [items])

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(items.map(stableStringify)))
  }, [items])

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const selectedItems = useMemo(() => {
    return items.filter((item) => isSelected(item))
  }, [items, isSelected])

  const selectionState = useMemo(
    () => ({
      selectedItems,
      selectedIds: [...selectedIds.values()],
      isSelected,
      toggle,
      toggleAll,
      selectAll,
      clearSelection,
    }),
    [
      selectedItems,
      selectedIds,
      isSelected,
      toggle,
      toggleAll,
      selectAll,
      clearSelection,
    ]
  )

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectionState)
    }
  }, [onSelectionChange, selectionState])

  return selectionState
}
